import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { UPDATE_TEMPORARY_LIVING_ASSIGNMENT_FAILURE } from 'modules/authorizations/store/types/temporaryLivingTypes';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import {
  getTemporaryLivingAssignments,
  updateTemporaryLivingAssignments
} from 'modules/authorizations/store/actions/temporaryLiving.actions';
import EditableCard from 'modules/common/editableCard.component';
import React, { useMemo, useState } from 'react';
import { TRANSFEREE_FAMILY_MEMBER_TYPE_ID } from 'modules/enumerations/enumerationIds';
import { getUsStateOptions } from './utils';

const NULL_GUID = '00000000-0000-0000-0000-000000000000';

const getStatusIdByTitle = (enumerations, title) => {
  const statuses = Array.isArray(enumerations?.temporaryLivingStatuses) ? enumerations.temporaryLivingStatuses : [];
  return statuses.find(s => s.title === title)?.id ?? NULL_GUID;
};

const getDerivedTempLivingStatusId = (assignment, enumerations) => {
  if (assignment?.canceledDate) return getStatusIdByTitle(enumerations, 'Canceled');
  if (assignment?.onHoldDate) return getStatusIdByTitle(enumerations, 'On Hold');
  if (assignment?.movedOutDate) return getStatusIdByTitle(enumerations, 'Moved Out');
  if (assignment?.noticeGiven) return getStatusIdByTitle(enumerations, 'Notice to Vacate Sent');
  if (assignment?.movedInDate) return getStatusIdByTitle(enumerations, 'Moved In');
  if (assignment?.confirmedDate) return getStatusIdByTitle(enumerations, 'Confirmed');
  if (assignment?.optionsSentDate) return getStatusIdByTitle(enumerations, 'Options Sent');
  if (assignment?.requestedDate) return getStatusIdByTitle(enumerations, 'Requested');
  return getStatusIdByTitle(enumerations, 'Initiated');
};

const PreferencesCard = props => {
  const [isLoading, setIsLoading] = useState(false);

  const { tempLivingAssignment, familyMembers, pets, enumerations, authId, disableEdit } = props;
  const tempLivingInfo = tempLivingAssignment.temporaryLivingAssignment;

  const tabs = [{ header: 'Preferences', order: 1, iconPath: ['fas', 'user'] }];

  // const relationshipOptions =
  //   enumerations && enumerations.familyRelationshipTypes && enumerations.familyRelationshipTypes.length > 0
  //     ? enumerations.familyRelationshipTypes.filter(s => s.isActive === true)
  //     : [{ id: NULL_GUID, title: '' }];

  const assignmentTypes =
    enumerations && enumerations.assignmentTypes && enumerations.assignmentTypes.length > 0
      ? enumerations.assignmentTypes.filter(s => s.isActive === true)
      : [{ id: NULL_GUID, title: '' }];

  const costCapOptions =
    enumerations && enumerations.costCapOptions && enumerations.costCapOptions.length > 0
      ? enumerations.costCapOptions.filter(s => s.isActive === true)
      : [{ id: NULL_GUID, title: '' }];

  const bookingSources =
    enumerations && enumerations.bookingSources && enumerations.bookingSources.length > 0
      ? enumerations.bookingSources.filter(s => s.isActive === true)
      : [{ id: NULL_GUID, title: '' }];

  const unitTypes =
    enumerations && enumerations.unitTypes && enumerations.unitTypes.length > 0
      ? enumerations.unitTypes.filter(s => s.isActive === true)
      : [{ id: NULL_GUID, title: '' }];

  const provinces = getUsStateOptions(enumerations?.provinces);

  const familyMembersSafe = Array.isArray(familyMembers) ? familyMembers : [];
  const petsSafe = Array.isArray(pets) ? pets : [];

  // Only the date part is compared. A saved date comes back as 'YYYY-MM-DDT00:00:00', which parses as local time, while
  // one just picked in the edit modal is 'YYYY-MM-DD', which parses as UTC, and mixing the two could be off by a day.
  const calculateDaysRequested = (estimatedMoveIn, estimatedMoveOut) => {
    if (!estimatedMoveIn || !estimatedMoveOut) return 0;
    const startDate = Date.parse(String(estimatedMoveIn).slice(0, 10));
    const endDate = Date.parse(String(estimatedMoveOut).slice(0, 10));
    if (isNaN(startDate) || isNaN(endDate)) return 0;
    const diffInMs = endDate - startDate;
    return Math.max(Math.ceil(diffInMs / (1000 * 60 * 60 * 24)) + 1, 0);
  };

  const toDecimalOrNull = value => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const toDateOrNull = value => (value === '' || value === undefined ? null : value);

  const currentSelectedFamily = useMemo(() => {
    if (Array.isArray(tempLivingAssignment?.familyMembers)) {
      return familyMembersSafe
        .filter(f => tempLivingAssignment.familyMembers.some(x => x.familyMemberId === f.familyMemberId))
        .map(f => ({ id: f.familyMemberId, title: `${f.firstName} ${f.lastName}` }));
    }
    return [];
  }, [tempLivingAssignment, familyMembersSafe]);

  const currentSelectedPets = useMemo(() => {
    if (Array.isArray(tempLivingAssignment?.pets)) {
      return petsSafe.filter(p => tempLivingAssignment.pets.some(x => x.petId === p.petsId)).map(p => ({ id: p.petsId, title: p.name }));
    }
    return [];
  }, [tempLivingAssignment, petsSafe]);

  const summaries = [
    {
      header: 'Assignment Type',
      accessorKey: 'assignmentType',
      type: 'dropdown-v2',
      options: assignmentTypes,
      required: false,
      value: tempLivingInfo ? tempLivingInfo.assignmentType ?? NULL_GUID : NULL_GUID,
      tab: 1
    },
    {
      header: 'Temp Living City',
      accessorKey: 'tempLivingCity',
      type: 'text',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.tempLivingCity ?? '' : '',
      tab: 1
    },
    {
      header: 'Temp Living State',
      accessorKey: 'tempLivingStateId',
      type: 'dropdown-v2',
      options: provinces,
      required: false,
      value: tempLivingInfo ? tempLivingInfo.tempLivingStateId ?? NULL_GUID : NULL_GUID,
      tab: 1
    },
    {
      header: 'Estimated Move In',
      accessorKey: 'estimatedMoveIn',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.estimatedMoveIn ?? null : null,
      tab: 1
    },
    {
      header: 'Estimated Move Out',
      accessorKey: 'estimatedMoveOut',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.estimatedMoveOut ?? null : null,
      tab: 1
    },
    {
      header: 'Days Requested',
      accessorKey: 'daysRequested',
      type: 'number',
      required: false,
      value: tempLivingInfo ? calculateDaysRequested(tempLivingInfo.estimatedMoveIn, tempLivingInfo.estimatedMoveOut) : 0,
      hasDefault: true,
      defaultValue: ({ values }) => calculateDaysRequested(values.estimatedMoveIn, values.estimatedMoveOut),
      disabled: true,
      tab: 1
    },
    {
      header: 'Days Allowed',
      accessorKey: 'daysAllowed',
      type: 'number',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.daysAllowed ?? 0 : 0,
      tab: 1
    },
    {
      header: 'Total Cost Cap',
      accessorKey: 'costCap',
      type: 'decimal',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.costCap : null,
      tab: 1
    },
    {
      header: 'Daily Cost Cap',
      accessorKey: 'dailyCostCap',
      type: 'decimal',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.dailyCostCap ?? null : null,
      tab: 1
    },
    {
      header: 'Cost Cap Options',
      accessorKey: 'costCapOptionId',
      type: 'dropdown-v2',
      options: costCapOptions,
      required: false,
      value: tempLivingInfo ? tempLivingInfo.costCapOptionId ?? NULL_GUID : NULL_GUID,
      tab: 1
    },
    {
      header: 'Booking Source',
      accessorKey: 'bookingSourceId',
      type: 'dropdown-v2',
      options: bookingSources,
      required: false,
      value: tempLivingInfo ? tempLivingInfo.bookingSourceId ?? NULL_GUID : NULL_GUID,
      tab: 1
    },
    {
      header: 'Unit Type',
      accessorKey: 'unitTypeId',
      type: 'dropdown-v2',
      options: unitTypes,
      required: false,
      value: tempLivingInfo ? tempLivingInfo.unitTypeId ?? NULL_GUID : NULL_GUID,
      tab: 1
    },
    {
      header: 'Family',
      accessorKey: 'family',
      type: 'multi-select-v2',
      options: familyMembersSafe
        .filter(f => f.relationship !== TRANSFEREE_FAMILY_MEMBER_TYPE_ID)
        .map(f => ({
          id: f.familyMemberId,
          title: `${f.firstName} ${f.lastName}`
        })),
      required: false,
      value: currentSelectedFamily,
      tab: 1
    },
    {
      header: 'Pets',
      accessorKey: 'pets',
      type: 'multi-select-v2',
      options: petsSafe.map(p => ({
        id: p.petsId,
        title: p.name
      })),
      required: false,
      value: currentSelectedPets,
      tab: 1
    },
    {
      header: 'Number of Bedrooms',
      accessorKey: 'numberOfBedrooms',
      type: 'number',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.numberOfBedrooms ?? 0 : 0,
      tab: 1
    },
    {
      header: 'Special Needs',
      accessorKey: 'specialNeeds',
      type: 'text',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.specialNeeds ?? '' : '',
      tab: 1
    },
    {
      header: 'Requested Date',
      accessorKey: 'requestedDate',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.requestedDate ?? null : null,
      tab: 1
    },
    {
      header: 'Options Sent Date',
      accessorKey: 'optionsSentDate',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.optionsSentDate ?? null : null,
      tab: 1
    },
    {
      header: 'Confirmed Date',
      accessorKey: 'confirmedDate',
      type: 'date', 
      required: false,
      value: tempLivingInfo ? tempLivingInfo.confirmedDate ?? null : null,
      tab: 1
    }
  ];

  const updateFields = async (tab, values) => {
    setIsLoading(true);

    const newValues = { ...tempLivingAssignment };

    newValues.temporaryLivingAssignment.assignmentType = values.assignmentType ?? NULL_GUID;
    newValues.temporaryLivingAssignment.tempLivingCity = values.tempLivingCity ?? '';
    newValues.temporaryLivingAssignment.tempLivingStateId = values.tempLivingStateId ?? NULL_GUID;
    newValues.temporaryLivingAssignment.estimatedMoveIn = toDateOrNull(values.estimatedMoveIn);
    newValues.temporaryLivingAssignment.estimatedMoveOut = toDateOrNull(values.estimatedMoveOut);
    newValues.temporaryLivingAssignment.daysRequested = calculateDaysRequested(values.estimatedMoveIn, values.estimatedMoveOut);
    newValues.temporaryLivingAssignment.daysAllowed = values.daysAllowed ?? null;
    newValues.temporaryLivingAssignment.costCap = toDecimalOrNull(values.costCap);
    newValues.temporaryLivingAssignment.dailyCostCap = toDecimalOrNull(values.dailyCostCap);
    newValues.temporaryLivingAssignment.costCapOptionId = values.costCapOptionId ?? NULL_GUID;
    newValues.temporaryLivingAssignment.bookingSourceId = values.bookingSourceId ?? NULL_GUID;
    newValues.temporaryLivingAssignment.unitTypeId = values.unitTypeId ?? NULL_GUID;
    newValues.temporaryLivingAssignment.numberOfBedrooms = values.numberOfBedrooms ?? null;
    newValues.temporaryLivingAssignment.specialNeeds = values.specialNeeds ?? '';
    newValues.temporaryLivingAssignment.requestedDate = toDateOrNull(values.requestedDate);
    newValues.temporaryLivingAssignment.optionsSentDate = toDateOrNull(values.optionsSentDate);
    newValues.temporaryLivingAssignment.confirmedDate = toDateOrNull(values.confirmedDate);
    newValues.temporaryLivingAssignment.statusId = getDerivedTempLivingStatusId(newValues.temporaryLivingAssignment, enumerations);

    const famIds = Array.isArray(values.family) ? values.family.map(s => s.id) : [];
    const petIds = Array.isArray(values.pets) ? values.pets.map(s => s.id) : [];
    newValues.familyMembers = famIds.map(id => ({ familyMemberId: id }));
    newValues.pets = petIds.map(id => ({ petId: id }));

    const tliId = tempLivingAssignment.temporaryLivingAssignmentId;

    if (authId && tliId) {
      const resp = await props.updateTemporaryLivingAssignments(authId, tliId, newValues);
      if (resp.type === UPDATE_TEMPORARY_LIVING_ASSIGNMENT_FAILURE) {
        props.showToast('Failed to update the assignment info, please try again.', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR
        });
        setIsLoading(false);
        return false;
      }

      await props.getTemporaryLivingAssignments(authId);

      setIsLoading(false);
      return true;
    } else {
      setIsLoading(false);
      return false;
    }
  };

  return (
    <EditableCard
      tabs={tabs}
      summaries={summaries}
      numColumns={5}
      updateFields={updateFields}
      isLoading={isLoading}
      hideEdit={disableEdit}
    />
  );
};

export default compose(
  connect(null, {
    updateTemporaryLivingAssignments,
    getTemporaryLivingAssignments,
    showToast
  })
)(PreferencesCard);
